// TaskImportPOC.js — Documentation Fonctionnelle : POC « Import de tâches » (module Environnement).
// Aperçu interactif de l'écran réel d'import de tâches : 4 étapes (sélection de l'environnement →
// upload des fichiers JSON → validation & prévisualisation → import). Les actions sont SIMULÉES
// localement (démo, aucun appel réseau). Source de vérité : automata-studio-frontend
// [AUT][08]-TaskImport.js + automata_environment (TaskController / FunctionController / EnvironmentController).
import React, { useState } from 'react';
import {
  Upload, CheckCircle, AlertTriangle, Server, Eye, Download, Database, Settings,
  FileCheck, FileText, AlertCircle, Info, ArrowRight, ArrowLeft, HardDrive, Code,
  X, RefreshCw, CloudUpload, ChevronDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// Environnements proposés (données en dur, calquées sur l'écran réel).
const ENVIRONMENTS = [
  { id: '1', appName: 'BGFI-Recette', appType: 'GENERO_GDC', appVersion: 'v11', active: true, functions: ['VIR_INIT', 'VIR_VALID', 'CPT_CONSULT'] },
  { id: '2', appName: 'Amplitude-Prod', appType: 'GENERO_GDC', appVersion: 'v12', active: true, functions: ['PRELEV_INIT', 'CHQ_REMISE'] },
  { id: '3', appName: 'Sandbox-API', appType: 'API_REST', appVersion: 'v1', active: false, functions: ['HEALTH_CHECK'] },
];

// Fichiers JSON simulés (chacun = un scénario / une tâche détectée).
// matchFn = nom de fonction attendu dans le fichier (sert à la validation contre l'environnement).
const SOURCE_FILES = [
  { fileName: 'task_virement_init.json', sizeKb: 4.2, taskName: 'Initiation virement', matchFn: 'VIR_INIT', taskType: 'FONCTIONNEL', taskNature: 'NOMINAL', interactions: 12, warning: null },
  { fileName: 'task_virement_valid.json', sizeKb: 3.1, taskName: 'Validation virement', matchFn: 'VIR_VALID', taskType: 'FONCTIONNEL', taskNature: 'NOMINAL', interactions: 8, warning: null },
  { fileName: 'task_consult_compte.json', sizeKb: 2.0, taskName: 'Consultation compte', matchFn: 'CPT_CONSULT', taskType: 'FONCTIONNEL', taskNature: 'NOMINAL', interactions: 5, warning: 'Description de tâche manquante' },
  { fileName: 'task_prelevement.json', sizeKb: 3.6, taskName: 'Prélèvement SEPA', matchFn: 'PRELEV_INIT', taskType: 'FONCTIONNEL', taskNature: 'NOMINAL', interactions: 9, warning: null },
  { fileName: 'task_corrompu.json', sizeKb: 1.1, taskName: null, matchFn: null, taskType: null, taskNature: null, interactions: 0, warning: null, brokenJson: true },
];

// Étapes du wizard, fidèles à l'écran réel (label + description).
const STEPS = [
  { label: "Sélection de l'environnement", description: "Choisissez l'environnement de destination", icon: Server },
  { label: 'Upload des fichiers JSON', description: 'Téléchargez un ou plusieurs fichiers de tâches', icon: Upload },
  { label: 'Validation et prévisualisation', description: 'Vérification et aperçu avant import', icon: Eye },
  { label: 'Import des tâches', description: 'Création des tâches dans le système', icon: Download },
];

// ---- Petits sous-composants de la réplique ---------------------------------
const StepIcon = ({ icon: IconCmp, state }) => (
  <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 transition-all ${
    state === 'done' ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white'
      : state === 'active' ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-lg shadow-blue-500/25'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}>
    {state === 'done' ? <CheckCircle className="h-4 w-4" /> : <IconCmp className="h-4 w-4" />}
  </span>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

const StatBox = ({ value, label, color }) => (
  <div className="p-3 rounded-xl border text-center" style={{ backgroundColor: `${color}14`, borderColor: `${color}33` }}>
    <p className="text-2xl font-bold" style={{ color }}>{value}</p>
    <p className="text-xs" style={{ color }}>{label}</p>
  </div>
);

// ---- Page -------------------------------------------------------------------
const TaskImportPOC = () => {
  const [step, setStep] = useState(0); // 0=env, 1=upload, 2=prévisualisation, 3=résultat
  const [envId, setEnvId] = useState('');
  const [uploaded, setUploaded] = useState([]); // noms de fichiers « uploadés »
  const [expanded, setExpanded] = useState(null); // fichier déplié dans l'aperçu
  const [search, setSearch] = useState('');

  const env = ENVIRONMENTS.find((e) => e.id === envId) || null;

  // Validation simulée : valide si JSON correct et fonction présente dans l'env choisi.
  const evaluate = (f) => {
    if (f.brokenJson) return { status: 'error', detail: 'Format JSON invalide' };
    if (!env) return { status: 'pending', detail: "En attente d'un environnement" };
    if (!env.functions.includes(f.matchFn)) return { status: 'error', detail: `Aucune fonction « ${f.matchFn} » dans ${env.appName}` };
    if (f.warning) return { status: 'warning', detail: f.warning };
    return { status: 'ok', detail: 'Prêt à importer' };
  };

  const parsedFiles = SOURCE_FILES.filter((f) => uploaded.includes(f.fileName));
  const filtered = parsedFiles.filter((f) =>
    `${f.fileName} ${f.taskName || ''} ${f.matchFn || ''}`.toLowerCase().includes(search.toLowerCase()));

  const validFiles = parsedFiles.filter((f) => evaluate(f).status === 'ok' || evaluate(f).status === 'warning');
  const errorFiles = parsedFiles.filter((f) => evaluate(f).status === 'error');

  const simulateUpload = () => setUploaded(SOURCE_FILES.map((f) => f.fileName));
  const removeFile = (name) => setUploaded((prev) => prev.filter((n) => n !== name));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const reset = () => {
    setStep(0); setEnvId(''); setUploaded([]); setExpanded(null); setSearch('');
  };

  const stepState = (i) => (step > i ? 'done' : step === i ? 'active' : 'todo');

  // Garde la navigation « Suivant » cohérente avec le réel.
  const nextDisabled =
    (step === 0 && !env) ||
    (step === 1 && parsedFiles.length === 0);

  return (
    <div className="space-y-8">
      {/* ===== Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Import de tâches</h1>
          <Badge color="#3B82F6">Module Environnement</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran « Import de tâches » (démo, actions simulées localement).
        </p>
      </div>

      {/* ===== Réplique interactive de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête type app : titre + bouton actualiser + bouton retour */}
        <div className="px-5 py-4 bg-gradient-to-r from-blue-500 to-indigo-700 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center"><Upload className="h-5 w-5" /></span>
            <div>
              <h2 className="text-lg font-bold leading-tight">Import de Tâches</h2>
              <p className="text-xs text-blue-100">Importez des définitions de tâches depuis des fichiers JSON</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={reset} title="Actualiser" className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={reset} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-white text-sm font-medium"><ArrowLeft className="h-4 w-4" />Retour</button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Panneau gauche : stepper (label + description) */}
          <div className="lg:w-[300px] shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Processus d'import</h3>
            <div className="space-y-0">
              {STEPS.map((s, i) => {
                const allowed = i <= step || (i === 1 && env) || i === 0;
                return (
                  <div key={i}>
                    <button onClick={() => allowed && setStep(i)} disabled={!allowed}
                      className="flex items-start gap-3 w-full text-left disabled:cursor-not-allowed">
                      <StepIcon icon={s.icon} state={stepState(i)} />
                      <div className="min-w-0 pt-0.5">
                        <p className={`text-sm font-medium ${step === i ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>{s.label}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{s.description}</p>
                      </div>
                    </button>
                    {i < STEPS.length - 1 && <div className="ml-[18px] w-px h-6 bg-gray-200 dark:bg-gray-700 my-1" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Panneau droit : contenu de l'étape */}
          <div className="flex-1 p-5 min-w-0">
            {/* ----- Étape 0 : Sélection de l'environnement ----- */}
            {step === 0 && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Server className="h-5 w-5 text-blue-500" />
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Sélection de l'environnement</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Choisissez l'environnement dans lequel importer les tâches. Les fonctions seront recherchées dans cet environnement.</p>
                <div className="space-y-2">
                  {ENVIRONMENTS.map((e) => {
                    const on = envId === e.id;
                    return (
                      <button key={e.id} onClick={() => setEnvId(e.id)}
                        className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${on ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
                        <span className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shrink-0"><HardDrive className="h-5 w-5 text-white" /></span>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm text-gray-900 dark:text-white">{e.appName}</div>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <Badge color="#6366F1">{e.appType}</Badge>
                            <Badge color="#8B5CF6">{e.appVersion}</Badge>
                            {e.active && <Badge color="#10B981">Actif</Badge>}
                          </div>
                        </div>
                        {on && <CheckCircle className="h-5 w-5 text-blue-500 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                {env && (
                  <div className="mt-4 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Environnement sélectionné</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-0.5">{env.appName}</p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{env.functions.length} fonction(s) disponible(s)</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ----- Étape 1 : Upload des fichiers JSON ----- */}
            {step === 1 && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Upload className="h-5 w-5 text-blue-500" />
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Upload des fichiers JSON</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Sélectionnez ou glissez-déposez un ou plusieurs fichiers JSON.</p>

                {/* Zone de dépôt (simulée) */}
                <button onClick={simulateUpload}
                  className="block w-full border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 rounded-2xl p-8 text-center cursor-pointer transition-colors">
                  <span className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto mb-4"><CloudUpload className="h-8 w-8 text-white" /></span>
                  <p className="text-base font-medium text-gray-900 dark:text-white">Cliquez ou glissez-déposez des fichiers JSON</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Formats acceptés : .json &middot; Sélection multiple autorisée</p>
                </button>

                {parsedFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{parsedFiles.length} fichier(s)</span>
                      <div className="flex items-center gap-3">
                        {validFiles.length > 0 && <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400"><CheckCircle className="h-3 w-3" />{validFiles.length} valide(s)</span>}
                        {errorFiles.length > 0 && <span className="flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400"><AlertCircle className="h-3 w-3" />{errorFiles.length} erreur(s)</span>}
                      </div>
                    </div>
                    {parsedFiles.map((f) => {
                      const ev = evaluate(f);
                      const isErr = ev.status === 'error';
                      return (
                        <div key={f.fileName} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                          isErr ? 'border-red-200 dark:border-red-800/50 bg-red-50/50 dark:bg-red-900/10'
                            : ev.status === 'warning' ? 'border-amber-200 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-900/10'
                            : 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-900/10'}`}>
                          <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${isErr ? 'bg-red-100 dark:bg-red-900/30 text-red-500' : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500'}`}>
                            {isErr ? <AlertCircle className="h-4 w-4" /> : <FileCheck className="h-4 w-4" />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{f.fileName}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {f.sizeKb} KB
                              {f.taskName && <> &middot; {f.taskName}</>}
                              {!f.brokenJson && <> &middot; {f.interactions} interactions</>}
                            </p>
                            {isErr && <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 truncate">{ev.detail}</p>}
                          </div>
                          <button onClick={() => removeFile(f.fileName)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ----- Étape 2 : Validation et prévisualisation ----- */}
            {step === 2 && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="h-5 w-5 text-blue-500" />
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Validation et prévisualisation</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Vérification des tâches détectées — cible : <strong>{env?.appName}</strong>.</p>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <StatBox value={parsedFiles.length} label="Total" color="#3B82F6" />
                  <StatBox value={validFiles.length} label="Prêts" color="#10B981" />
                  <StatBox value={errorFiles.length} label="Erreurs" color="#EF4444" />
                </div>

                {errorFiles.length > 0 && validFiles.length > 0 && (
                  <div className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-700 dark:text-amber-300">{errorFiles.length} fichier(s) en erreur seront ignorés.</p>
                  </div>
                )}

                <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un fichier / une tâche…" className="mb-3" />

                <div className="space-y-2 max-h-[440px] overflow-y-auto">
                  {filtered.map((f) => {
                    const ev = evaluate(f);
                    const hasErr = ev.status === 'error';
                    const open = expanded === f.fileName;
                    return (
                      <div key={f.fileName} className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <button onClick={() => setExpanded(open ? null : f.fileName)}
                          className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                          <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 text-white ${hasErr ? 'bg-gradient-to-br from-red-500 to-red-600' : 'bg-gradient-to-br from-emerald-500 to-emerald-600'}`}>
                            {hasErr ? <AlertCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{f.fileName}</span>
                              {ev.status === 'ok' && <Badge color="#10B981">Prêt</Badge>}
                              {ev.status === 'warning' && <Badge color="#F59E0B">Avertissement</Badge>}
                              {hasErr && <Badge color="#EF4444">Erreur</Badge>}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {f.taskName || 'N/A'}
                              {!f.brokenJson && <> &middot; {f.interactions} interactions</>}
                              {f.matchFn && <> &middot; {f.matchFn}</>}
                            </p>
                          </div>
                          <ChevronDown className={`h-4 w-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </button>
                        {open && (
                          <div className="px-3 pb-3 pt-3 space-y-3 border-t border-gray-100 dark:border-gray-700">
                            {hasErr && (
                              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                <p className="text-xs font-semibold text-red-900 dark:text-red-200 mb-1 uppercase tracking-wider">Erreurs</p>
                                <div className="flex items-start gap-2"><AlertCircle className="h-3 w-3 text-red-500 mt-0.5 shrink-0" /><p className="text-sm text-red-700 dark:text-red-300">{ev.detail}</p></div>
                              </div>
                            )}
                            {ev.status === 'warning' && (
                              <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 mb-1 uppercase tracking-wider">Avertissements</p>
                                <div className="flex items-start gap-2"><Info className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" /><p className="text-sm text-amber-700 dark:text-amber-300">{ev.detail}</p></div>
                              </div>
                            )}
                            {!f.brokenJson && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700">
                                  <div className="flex items-center gap-1.5 mb-2"><Settings className="h-3.5 w-3.5 text-blue-500" /><span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">Tâche</span></div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">{f.taskName}</p>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    <Badge color="#6366F1">{f.taskType}</Badge>
                                    <Badge color="#8B5CF6">{f.taskNature}</Badge>
                                  </div>
                                </div>
                                <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700">
                                  <div className="flex items-center gap-1.5 mb-2"><Database className="h-3.5 w-3.5 text-teal-500" /><span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">Fonction</span></div>
                                  {ev.status !== 'error'
                                    ? <code className="text-xs text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono inline-block">{f.matchFn}</code>
                                    : <p className="text-sm text-red-600 dark:text-red-400">Non trouvée</p>}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filtered.length === 0 && <EmptyState icon={FileText} title="Aucun fichier" message="Aucun fichier ne correspond à la recherche." />}
                </div>
              </div>
            )}

            {/* ----- Étape 3 : Résultat de l'import ----- */}
            {step === 3 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  {validFiles.length > 0 ? <CheckCircle className="h-5 w-5 text-emerald-500" /> : <AlertTriangle className="h-5 w-5 text-red-500" />}
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Résultat de l'import</h3>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <StatBox value={parsedFiles.length} label="Total" color="#3B82F6" />
                  <StatBox value={validFiles.length} label="Succès" color="#10B981" />
                  <StatBox value={errorFiles.length} label="Échecs" color="#EF4444" />
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="overflow-auto max-h-72">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                        <tr>{['', 'Fichier', 'Tâche', 'Inter.', 'Détail'].map((h, i) => <th key={i} className="text-left font-semibold px-3 py-2 text-gray-600 dark:text-gray-300">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {parsedFiles.map((f, i) => {
                          const ok = evaluate(f).status === 'ok' || evaluate(f).status === 'warning';
                          return (
                            <tr key={i} className={ok ? '' : 'bg-red-50/50 dark:bg-red-900/10'}>
                              <td className="px-3 py-2">{ok ? <CheckCircle className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-red-500" />}</td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-300 truncate max-w-[160px]">{f.fileName}</td>
                              <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{f.taskName || f.fileName}</td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{ok ? f.interactions : '—'}</td>
                              <td className="px-3 py-2 text-xs">{ok
                                ? <span className="text-emerald-600 dark:text-emerald-400">ID simulé : #{1000 + i}</span>
                                : <span className="text-red-600 dark:text-red-400">{evaluate(f).detail}</span>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ----- Barre de navigation (Précédent / Suivant / Importer / Nouvel import) ----- */}
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
              <Button variant="ghost" onClick={prev} disabled={step === 0}><ArrowLeft className="h-4 w-4" />Précédent</Button>
              <div className="flex items-center gap-2">
                {step < 2 && (
                  <Button onClick={next} disabled={nextDisabled}>Suivant<ArrowRight className="h-4 w-4" /></Button>
                )}
                {step === 2 && (
                  <Button onClick={() => setStep(3)} disabled={validFiles.length === 0}>
                    <Download className="h-4 w-4" />Importer{validFiles.length > 0 ? ` (${validFiles.length})` : ''}
                  </Button>
                )}
                {step === 3 && (
                  <Button onClick={reset}><RefreshCw className="h-4 w-4" />Nouvel import</Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Server className="h-5 w-5 text-blue-500" />1. Choisir l'environnement</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Ouvrir l'écran <strong>Import de tâches</strong> depuis le module Environnement.</GuideStep>
              <GuideStep n={2}>Sélectionner l'<strong>environnement</strong> de destination (les fonctions y seront recherchées). <button onClick={() => setStep(0)} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={3}>Cliquer sur <strong>Suivant</strong> pour passer à l'upload.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Upload className="h-5 w-5 text-blue-500" />2. Uploader & prévisualiser</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Glisser-déposer ou sélectionner un ou plusieurs <strong>fichiers JSON</strong>. <button onClick={() => { if (!env) setEnvId('1'); setStep(1); }} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Passer à la <strong>prévisualisation</strong> : déplier une tâche pour voir tâche, fonction et interactions. <button onClick={() => { if (!env) setEnvId('1'); simulateUpload(); setStep(2); }} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={3}>Vérifier le statut : <span className="text-emerald-600">Prêt</span>, <span className="text-amber-600">Avertissement</span> ou <span className="text-red-600">Erreur</span>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Download className="h-5 w-5 text-blue-500" />3. Importer</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Importer »</strong> pour créer les tâches valides. <button onClick={() => { if (!env) setEnvId('1'); simulateUpload(); setStep(3); }} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Lire le <strong>récapitulatif</strong> : total, succès, échecs et détail par tâche.</GuideStep>
              <GuideStep n={3}>Relancer un <strong>nouvel import</strong> si besoin.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence : structure attendue du fichier ===== */}
      <Card>
        <CardHeader><CardTitle><span className="flex items-center gap-2"><Code className="h-5 w-5 text-blue-500" />Structure attendue du fichier JSON</span></CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Champ', 'Niveau', 'Requis', 'Détails'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {[
                  ['task', 'racine', 'Oui', 'Objet racine — sa présence valide la structure'],
                  ['task.function_name', 'task', 'Oui', "Doit correspondre à une fonction de l'environnement choisi"],
                  ['task.taskName', 'task', 'Oui', 'Libellé de la tâche'],
                  ['task.taskDescription', 'task', 'Non', 'Vide = avertissement (import autorisé)'],
                  ['task.taskType / taskNature', 'task', 'Non', 'Défaut UNDEFINED si absent'],
                  ['task.interactions[]', 'task', 'Non', 'Chaque item : interactionId, sequence, type (requis)'],
                ].map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-mono text-xs">{r[0]}</td>
                    <td className="px-4 py-2">{r[1]}</td>
                    <td className="px-4 py-2">{r[2] === 'Oui' ? <Badge color="#EF4444">Oui</Badge> : <Badge>Non</Badge>}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ===== Règles de gestion + Endpoints API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Settings className="h-5 w-5 text-blue-500" />Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Un <strong>environnement</strong> doit être sélectionné avant l'upload et l'import.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />La <strong>fonction</strong> du fichier doit exister dans l'environnement cible, sinon la tâche est en erreur.</li>
            <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />Une description manquante génère un simple <strong>avertissement</strong> — l'import reste possible.</li>
            <li className="flex items-start gap-2"><X className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Un JSON invalide ou une fonction introuvable rend le fichier <strong>non importable</strong>.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Sélection <strong>multiple</strong> : plusieurs fichiers peuvent être importés en une passe.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Database className="h-5 w-5 text-blue-500" />Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/environments'],
              ['GET', '#10B981', '/api/automata/functions?environmentId='],
              ['POST', '#3B82F6', '/api/automata/tasks'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_environment / TaskController, FunctionController, EnvironmentController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default TaskImportPOC;
